import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import CaptionCard from "../components/CaptionCard.jsx";
import ReelCard from "../components/ReelCard.jsx";
import API from "../config/api.js";
import TemplateCard from "../components/TemplateCard.jsx";
// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function toVimeoEmbed(url) {
  if (!url) return "";

  if (url.includes("player.vimeo.com")) return url;

  try {
    const u = new URL(url);
    if (!u.hostname.includes("vimeo.com")) return url;

    const parts = u.pathname.replace(/^\/+/, "").split("/").filter(Boolean);

    if (parts.length === 0) return url;

    const videoId = parts[0];
    const hash    = parts[1];

    let embed = `https://player.vimeo.com/video/${videoId}`;
    if (hash) embed += `?h=${hash}`;
    return embed;
  } catch {
    return url.replace("vimeo.com/", "player.vimeo.com/video/");
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Lesson row
// ─────────────────────────────────────────────────────────────────────────────
function LessonRow({ lesson, onComplete }) {
  const [open, setOpen]           = useState(false);
  const [completing, setCompleting] = useState(false);

  const typeIcon = {
    video:       "fi-rr-play",
    file:        "fi-rr-file",
    "action text": "fi-rr-checkbox",
  }[lesson.type] || "fi-rr-play";

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const fd = new FormData();
      fd.append("idcoursemodulelesson", lesson.idcoursemodulelesson);
      await fetch(`${API.courses}?complete`, {
        method: "POST", credentials: "include", body: fd,
      });
      onComplete(lesson.idcoursemodulelesson);
    } catch (err) {
      console.error("Complete error:", err);
    } finally {
      setCompleting(false);
    }
  };

  // Pause vimeo iframes when closing
  const handleToggle = () => {
    if (open) {
      document.querySelectorAll(".course-lesson-video iframe").forEach((iframe) => {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      });
    }
    setOpen((v) => !v);
  };

  return (
    <div className="border rounded-4 p-4 mt-3">
      <div className="row">
        {/* Left: info */}
        <div className="col-md-5">
          {open && (
            <div className="mb-4">
              <button className="btn p-0 link-primary" onClick={handleToggle}>
                <i className="fi fi-rr-arrow-small-left" /> Back
              </button>
            </div>
          )}

          <h2 className="fs-6">{lesson.title}</h2>

          <span className="text-muted">
            <i className={`fi ${typeIcon} me-2`} style={{ verticalAlign: "text-bottom" }} />
            {lesson.type === "video" && <>Video <i className="fi fi-rr-clock ms-3 me-2" style={{ verticalAlign: "text-bottom" }} />{lesson.duration}</>}
            {lesson.type === "file" && "Workbook"}
            {lesson.type === "action text" && "Action"}
          </span>

          {open && (
            <div className="mt-3">
              {lesson.type !== "action text" && (
                <>
                  <p>{lesson.description}</p>
                  {lesson.url && lesson.type !== "file" && (
                    <a className="btn btn-secondary me-3" href={lesson.url} target="_blank" rel="noreferrer">
                      <i className="fi fi-rr-globe" /> Open
                    </a>
                  )}
                </>
              )}

              {!lesson.completed && lesson.type !== "action text" && (
                <button className="btn btn-light me-3" onClick={handleComplete} disabled={completing}>
                  <i className="fi fi-rr-badge-check" /> {completing ? "Saving..." : "Complete"}
                </button>
              )}

           {lesson.type === "file" && lesson.file && (
              <a
                href={`${API.baseUrl}/${lesson.file}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                Download
              </a>
            )}
            </div>
          )}
        </div>

        {/* Right: button / video */}
        <div className="col-md-7">
          {!open ? (
            <div className="text-end mt-2">
              <button
                type="button"
                className={`btn ${lesson.completed ? "btn-success" : "btn-secondary"} px-5`}
                onClick={handleToggle}
              >
                {lesson.url && lesson.type !== "file" && <i className="fi fi-rr-globe me-1" />}
                {lesson.completed ? "Completed" : "Start"}
              </button>
            </div>
          ) : (
            <>
              {lesson.type === "video" && lesson.video && (
                <div
                  className="course-lesson-video bg-light rounded-4 shadow border-0"
                  style={{ position: "relative", width: "100%", paddingBottom: "56.25%", overflow: "hidden" }}
                >
                  <iframe
                    src={lesson.video.includes("vimeo") ? toVimeoEmbed(lesson.video) : lesson.video}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    className="rounded-4"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={lesson.title}
                  />
                </div>
              )}

              {lesson.type === "action text" && (
                <div className="mt-3">
                  <p>{lesson.description}</p>
                  {!lesson.completed && (
                    <button className="btn btn-light me-3" onClick={handleComplete} disabled={completing}>
                      <i className="fi fi-rr-badge-check" /> {completing ? "Saving..." : "Complete"}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Course page
// ─────────────────────────────────────────────────────────────────────────────
export default function Course() {
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const idcourse = params.get("idcourse");

  const [course, setCourse]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("lessons");

  const loadCourse = useCallback(async () => {
    if (!idcourse) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("idcourse", idcourse);
      const res  = await fetch(`${API.courses}?load`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      setCourse(data);
    } catch (err) {
      console.error("Course load error:", err);
    } finally {
      setLoading(false);
    }
  }, [idcourse]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const handleComplete = (idcoursemodulelesson) => {
    setCourse((prev) => {
      const modules = prev.modules.map((mod) => ({
        ...mod,
        lessons: mod.lessons.map((l) =>
          l.idcoursemodulelesson === idcoursemodulelesson
            ? { ...l, completed: true }
            : l
        ),
      }));
      const lessonscompleted = prev.lessonscompleted + 1;
      const percentage = Math.round((lessonscompleted * 100) / prev.totalLessons);
      return { ...prev, modules, lessonscompleted, percentage };
    });
  };

  if (loading) {
    return (
      <AppLayout currentPage="courses">
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
        </div>
      </AppLayout>
    );
  }

  if (!course || course.error) {
    return (
      <AppLayout currentPage="courses">
        <div className="text-center py-5 text-muted">Course not found.</div>
      </AppLayout>
    );
  }

  const imageUrl = course.image
    ? `${API.baseUrl}/${course.image.replace("original.jpg", "624x427.jpg")}`
    : "/assets/img/624x427.png";

  const hasCaptions  = course.captions?.length  > 0;
  const hasReels     = course.reels?.length     > 0;
  const hasTemplates = course.templates?.length > 0;

  return (
    <AppLayout currentPage="courses">
      {/* Back link */}
      <div className="mb-4">
        <Link to="/courses" className="link-primary">
          <i className="fi fi-rr-arrow-small-left" /> Courses
        </Link>
      </div>

      {/* Hero */}
      <div className="row mb-4">
        <div className="col-md-8">
          <div className="p-5 bg-secondary-light h-100 rounded-5 d-flex flex-column justify-content-center">
            <h1 className="fs-5">{course.title}</h1>
            <div className="progress w-50 my-2" style={{ height: 5 }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${course.percentage}%` }}
                aria-valuenow={course.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="text-muted" style={{ whiteSpace: "pre-line" }}>
              {course.description}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <img
            src={imageUrl}
            alt={course.title}
            className="img-fluid w-100 bg-loading rounded-5"
            width="624"
            height="427"
          />
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3 mt-4 border-bottom" role="tablist">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "lessons" ? "active" : ""}`}
            onClick={() => setActiveTab("lessons")}
          >
            <i className="fi fi-rr-graduation-cap me-2" /> Lessons & Resources
          </button>
        </li>
        {hasCaptions && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "captions" ? "active" : ""}`}
              onClick={() => setActiveTab("captions")}
            >
              <i className="fi fi-rr-text me-2" /> Captions
            </button>
          </li>
        )}
        {hasReels && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "reels" ? "active" : ""}`}
              onClick={() => setActiveTab("reels")}
            >
              <i className="fi fi-rr-play-alt me-2" /> Reels
            </button>
          </li>
        )}
        {hasTemplates && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "templates" ? "active" : ""}`}
              onClick={() => setActiveTab("templates")}
            >
              <i className="fi fi-rr-layout-fluid me-2" /> Templates
            </button>
          </li>
        )}
      </ul>

      {/* Tab content */}
      {activeTab === "lessons" && (
        <div>
          {course.modules.map((mod) => (
            <div key={mod.idcoursemodule} className="mb-4">
              {mod.title && <h5 className="fw-bold mb-2">{mod.title}</h5>}
              {mod.lessons.map((lesson) => (
                <LessonRow
                  key={lesson.idcoursemodulelesson}
                  lesson={lesson}
                  onComplete={handleComplete}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {activeTab === "captions" && (
        <div className="row row-cols-1 row-cols-md-3 g-2 mt-3">
          {course.captions.map((caption) => (
            <div key={caption.idcaption} className="col">
              <CaptionCard caption={caption} />
            </div>
          ))}
        </div>
      )}

      {activeTab === "reels" && (
        <div className="row row-cols-2 row-cols-md-4 g-2 mt-3">
          {course.reels.map((reel) => (
            <div key={reel.idreel} className="col">
              <ReelCard reel={reel} isMyMedia={false} />
            </div>
          ))}
        </div>
      )}

{activeTab === "templates" && (
  <div className="row row-cols-2 row-cols-md-4 g-2 mt-3">
    {course.templates.map((template) => (
      <div key={template.idtemplate} className="col">
        <TemplateCard template={template} />
      </div>
    ))}
  </div>
)}
    </AppLayout>
  );
}